import React, { useCallback, useEffect } from 'react';
import Pencil from '@uber/base-icons/pencil';
import SpeechBubble from '@uber/base-icons/speech-bubble';
import { styled } from 'baseui';
import { type ButtonProps, Button, KIND, SHAPE, SIZE } from 'baseui/button';
import useShortcut from '../../../image/labelling/providers/hooks/use-shortcut';
import { handleShortcutRegistrationError } from '../../../image/labelling/providers/utils/shortcut';
import ShowHideButton from '../../collapsible-panel/trailing-toggle-component/show-hide-button.react';
import { VISIBILITY_ANNOTATION_ACTION } from '../../constant/constant';
import { useReviewMode } from '../../providers/review-mode-providers';
import type { ToggleVisibilityAnnotationPayloadT } from '../../side-panel/analytics/types';
import useAnalyticsForSidePanel from '../../side-panel/analytics/useAnalyticsForSidePanel';
import { STORE_TYPE } from '../../types/types';
import { useEditAnnotationHandlers, useReviewFeature } from '../hooks';
import { useReviewDrawer } from '../review-drawer';
import { useReviewModal } from '../review-modal/use-review-modal';
import { SectionHeader, StyledHeading } from '../styled-component-overrides';
import type { ReviewAnnotationT } from '../types';
import { isClassificationAnnotationId } from '../utils';
import { ObjectDetails } from './object-details';
type Props = {
  annotation: ReviewAnnotationT;
  annotationId: string;
};
const optionButtonProps: ButtonProps = {
  size: SIZE.mini,
  shape: SHAPE.square,
  kind: KIND.secondary
};
const OptionsContainer = styled('div', ({
  $theme
}) => ({
  display: 'flex',
  gap: $theme.sizing.scale300
}));
export const ObjectClassification = ({
  annotation,
  annotationId
}: Props) => {
  const {
    canEdit,
    enableCommentsFeedback
  } = useReviewFeature();
  const {
    openFeedbackPanel
  } = useReviewDrawer();
  const {
    addComment
  } = useReviewModal();
  const {
    onEditCallback,
    onInstanceDeleteCallback
  } = useEditAnnotationHandlers(canEdit);
  const {
    toggleAnnotationVisibility
  } = useReviewMode();
  const {
    registerHotKeys
  } = useShortcut();
  const handleAddComment = useCallback(() => {
    openFeedbackPanel();
    addComment(annotationId);
  }, [openFeedbackPanel, addComment, annotationId]);
  const {
    sendEventForToggleVisibilityAnnotation
  } = useAnalyticsForSidePanel();

  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const handleShowHideAnnotations = useCallback(() => {
    const updatedHidden = !annotation.hidden;
    toggleAnnotationVisibility?.(updatedHidden, annotationId, STORE_TYPE.ANNOTATION_STORE);
    let eventPayload: ToggleVisibilityAnnotationPayloadT = {
      objectType: annotation.type,
      label: annotation.label,
      objectId: annotationId,
      action: updatedHidden ? VISIBILITY_ANNOTATION_ACTION.HIDE : VISIBILITY_ANNOTATION_ACTION.SHOW
    };
    sendEventForToggleVisibilityAnnotation(eventPayload);
  }, [annotation.hidden, annotationId, toggleAnnotationVisibility, sendEventForToggleVisibilityAnnotation]);
  useEffect(() => {
    const {
      removeHotKeys,
      errors
    } = registerHotKeys([{
      keys: ['c'],
      onAction: event => {
        event.preventDefault();
        handleAddComment();
      }
    }, {
      keys: ['e'],
      onAction: () => onEditCallback?.(annotationId)
    }]);
    errors.forEach(error => handleShortcutRegistrationError(error));
    return () => {
      removeHotKeys();
    };
  }, [registerHotKeys, handleAddComment, openFeedbackPanel, addComment, annotationId, onEditCallback]);
  return <>
      <SectionHeader>
        <StyledHeading>{annotation.name}</StyledHeading>
        <OptionsContainer>
          <ShowHideButton hidden={!!annotation.hidden} onClick={handleShowHideAnnotations} disabled={isClassificationAnnotationId(annotationId)} />

          {enableCommentsFeedback && <Button {...optionButtonProps} onClick={handleAddComment} data-testid="add-comment-button">
              <SpeechBubble />
            </Button>}
          {onEditCallback && <Button {...optionButtonProps} onClick={() => onEditCallback(annotationId)} data-testid="edit-annotation-button">
              <Pencil />
            </Button>}
        </OptionsContainer>
      </SectionHeader>

      <ObjectDetails key={annotationId} annotation={annotation} {...onInstanceDeleteCallback && {
      onInstanceDelete: instanceIndex => onInstanceDeleteCallback(annotationId, instanceIndex)
    }} />
    </>;
};